import groupby from 'lodash.groupby';
import moment from 'moment';
import { TYPE_STRING, TYPE_COLLECTION, TYPE_DATE_COMPARISON, TYPE_ENUM, TYPE_BOOLEAN, TYPE_NUMBER } from './options/input-types';
import { convertFilterToApiFilter } from './filter-to-api-filter-converter';
import { lessThan, greaterThanOrEqualTo } from './operators/date';
import { isBlank, isNotBlank } from './operators/string';
import { isIn, isNull } from './operators/collection';
import { options } from './options';

export const filterTypeToApiFilterMap = {
  [TYPE_STRING]: {
    filterName: 'stringFilters',
    createFilter: ({ value, operator, apiFilter }) => {
      const isArrayWithValue = Array.isArray(value) && value.filter((v) => v).length > 0;
      const values = isArrayWithValue ? value.filter((v) => v.trim()) : [];
      if (values.length === 0 && !(operator.value === isBlank.value || operator.value === isNotBlank.value)) {
        return [];
      }
      return [
        {
          column: apiFilter,
          operator: operator.value,
          values,
        },
      ];
    },
  },
  [TYPE_COLLECTION]: {
    filterName: 'collectionFilters',
    createFilter: ({ value = null, operator, apiFilter }) => {
      const op = value ? isIn : isNull;
      return [
        {
          column: apiFilter,
          [op]: operator.value,
          values: value,
        },
      ];
    },
  },
  [TYPE_BOOLEAN]: {
    filterName: 'booleanFilters',
    createFilter: ({ value, apiFilter }) => {
      return [
        {
          column: apiFilter,
          value,
        },
      ];
    },
  },
  [TYPE_DATE_COMPARISON]: {
    filterName: 'dateTimeFilters',
    createFilter: ({ value, apiFilter }) => {
      const { from, to } = value;
      const toPlusaDay = moment(to).add(1, 'days').toISOString();
      const fromFilter =
        from != null
          ? [
              {
                column: apiFilter,
                operator: greaterThanOrEqualTo.value,
                value: from,
              },
            ]
          : [];
      const toFilter =
        to != null
          ? [
              {
                column: apiFilter,
                operator: lessThan.value,
                value: to !== from ? to : toPlusaDay,
              },
            ]
          : [];
      return [...fromFilter, ...toFilter];
    },
  },
  [TYPE_NUMBER]: {
    filterName: 'numericFilters',
    createFilter: ({ value = null, operator, apiFilter }) => {
      return [
        {
          column: apiFilter,
          operator: operator.value,
          values: value,
        },
      ];
    },
  },
  [TYPE_ENUM]: {
    createFilter: ({ value, apiFilter }) => [
      {
        [apiFilter]: value,
      },
    ],
  },
};

export const getApiFilterFromFilter = (filter, type) => {
  const { id, value, operator } = filter;
  const apiFilter = convertFilterToApiFilter(id);
  return filterTypeToApiFilterMap[type].createFilter({
    apiFilter,
    value,
    operator,
  });
};

export const apiFilterBuilder = (filters) => {
  // Filter out any filters that don't exist in view
  const validFilters = filters.filter((filter) => options[filter.id] != null);

  const groupedFilters = groupby(validFilters, (filter) => options[filter.id].type);
  const groupKeys = Object.keys(groupedFilters);
  const apiFilters = groupKeys.reduce((body, groupKey) => {
    const filterName = filterTypeToApiFilterMap[groupKey].filterName;
    const apiFilterGroup = groupedFilters[groupKey].reduce((acc, filter) => {
      const apiFilter = getApiFilterFromFilter(filter, groupKey);
      if (apiFilter) {
        return [...acc, ...apiFilter];
      }

      return acc;
    }, []);

    if (filterName) {
      return {
        ...body,
        [filterName]: apiFilterGroup,
      };
    }

    const filterGroup = apiFilterGroup.reduce((acc, filter) => {
      return { ...acc, ...filter };
    }, {});

    return {
      ...body,
      ...filterGroup,
    };
  }, {});

  return apiFilters;
};
